import { brakingOf, conveyorSpeed, propulsionOf, type SegmentSpec } from './segments';

/**
 * 操作の入力の 1 区間：時刻 `start` から `end` の前まで、接線方向（前向き）に `acceleration` の加速度を加える
 * （走行中にボタンを押している間の加速・ブースト。docs/physics/physics-model.md 4.6 節、ADR-0013）。
 *
 * 時刻はシミュレーションの時刻 [s]。`end` は `Infinity`（離すまで続ける）にできる。
 * 加速度は、ステップの始めの時刻で決め、そのステップの間は一定にする（区切りはステップ境界にそろう）。
 */
export interface ControlInterval {
  /** 開始時刻 [s] */
  readonly start: number;
  /** 終了時刻 [s]（この時刻は含まない。`Infinity` で終わりなし） */
  readonly end: number;
  /** 前向きの加速度 [m/s²]（0 以上） */
  readonly acceleration: number;
}

/** 操作の入力で加えられる加速度の上限 [m/s²]（約 5 G）。 */
export const MAX_CONTROL_ACCELERATION = 50;

/**
 * 操作の入力が効く区間か。リフト・ブレーキ・駅・発進加速の区間では、その区間の動きを優先し、入力は効かない。
 */
export function acceptsControl(spec: SegmentSpec | undefined): boolean {
  return (
    conveyorSpeed(spec) === undefined &&
    brakingOf(spec) === undefined &&
    propulsionOf(spec) === undefined
  );
}

/**
 * 時刻 `time` に加える操作の加速度 [m/s²]。重なる区間があれば、大きい方（足し合わせない）。
 * 入力がなければ 0。
 */
export function controlAccelerationAt(control: readonly ControlInterval[], time: number): number {
  let total = 0;
  for (const interval of control) {
    if (interval.start <= time && time < interval.end) {
      total = Math.max(total, interval.acceleration);
    }
  }
  return total;
}

/**
 * 操作の入力を検査する。
 *
 * @throws {RangeError} 時刻・加速度が不正な場合
 */
export function validateControl(control: readonly ControlInterval[]): void {
  for (const interval of control) {
    if (
      !Number.isFinite(interval.start) ||
      interval.start < 0 ||
      Number.isNaN(interval.end) ||
      !(interval.end > interval.start) ||
      !Number.isFinite(interval.acceleration) ||
      interval.acceleration < 0 ||
      interval.acceleration > MAX_CONTROL_ACCELERATION
    ) {
      throw new RangeError(
        `操作の入力が不正です（開始は 0 以上、終了は開始より後、加速度は 0〜${MAX_CONTROL_ACCELERATION} m/s²）`,
      );
    }
  }
}
